import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  UsersThree, Plus, X, CircleNotch, ArrowLeft, MapPin, CalendarBlank,
  Trash, PencilSimple, Check, LockKey, Globe,
} from "@phosphor-icons/react";
import api, { API } from "../lib/api";
import { useAuth } from "../lib/auth";
import UserLink from "../components/UserLink";
import AddToCalendarButton from "../components/AddToCalendarButton";
/**
 * MeetupsPage — casual gatherings with friends.
 * /app/meetups        → list (upcoming / mine / past tabs) + create
 * /app/meetups/:mid   → detail with attendees + join/leave + countdown
 */
export default function MeetupsPage() {
  const { mid } = useParams();
  return mid ? <MeetupDetail mid={mid}/> : <MeetupsList/>;
}


function MeetupsList() {
  const [items, setItems] = useState([]);
  const [scope, setScope] = useState("upcoming");
  const [loading, setLoading] = useState(true);
  const [urlParams, setUrlParams] = useSearchParams();
  const [createOpen, setCreateOpen] = useState(urlParams.get("new") === "1");
  const navigate = useNavigate();

  useEffect(() => {
    if (urlParams.get("new") === "1") {
      const next = new URLSearchParams(urlParams);
      next.delete("new");
      setUrlParams(next, { replace: true });
    }
  }, [urlParams, setUrlParams]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/meetups", { params: { scope } });
      setItems(data || []);
    } catch { setItems([]); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope]);

  const create = async (payload) => {
    const { data } = await api.post("/meetups", payload);
    setCreateOpen(false);
    navigate(`/app/meetups/${data.meetup_id}`);
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="meetups-list">
      <Header title="Meet-ups" sub="Command Center · Meetups"
              actionLabel="New Meet-up"
              onAction={() => setCreateOpen(true)}
              testid="meetups"/>

      <ScopeTabs scope={scope} onChange={setScope}
                 options={[["upcoming","Upcoming"],["mine","Mine"],["past","Past"]]}
                 testid="meetups-scope"/>

      {loading ? (
        <Loading/>
      ) : items.length === 0 ? (
        <Empty label="No meet-ups here yet." onCreate={() => setCreateOpen(true)} cta="Start a Meet-up" icon={UsersThree}/>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="meetups-grid">
          {items.map((m) => <MeetupTile key={m.meetup_id} meetup={m}/>)}
        </div>
      )}

      <AnimatePresence>
        {createOpen && <MeetupModal onClose={() => setCreateOpen(false)} onSave={create}/>}
      </AnimatePresence>
    </div>
  );
}


function MeetupTile({ meetup }) {
  return (
    <Link to={`/app/meetups/${meetup.meetup_id}`}
          data-testid={`meetup-tile-${meetup.meetup_id}`}
          className="rounded-2xl p-4 block transition-all hover:-translate-y-0.5"
          style={{
            background: "linear-gradient(180deg, rgba(26,18,6,0.75) 0%, rgba(5,3,0,0.9) 100%)",
            border: "1px solid rgba(212,175,55,0.45)",
            boxShadow: "0 6px 18px rgba(0,0,0,0.45)",
          }}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-display text-[18px] text-[#FFD700] leading-tight truncate">{meetup.title}</div>
          <div className="mt-1 text-[11px] uppercase tracking-widest text-[#F2DC8C]/60 flex items-center gap-1">
            <CalendarBlank size={11}/> {new Date(meetup.event_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
          </div>
          {meetup.location && (
            <div className="text-[11px] text-[#F2DC8C]/70 flex items-center gap-1 mt-0.5 truncate">
              <MapPin size={11}/> {meetup.location}
            </div>
          )}
        </div>
        <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
              style={{ background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)" }}>
          <UsersThree size={14} weight="fill" color="#0a0703"/>
        </span>
      </div>
      <div className="mt-3 flex items-center justify-between text-[10px] uppercase tracking-widest">
        <span className="text-[#F2DC8C]/60">
          {meetup.attendee_count} going · <UserLink username={meetup.owner_username} className="hover:text-[#FFD700]">@{meetup.owner_username}</UserLink>
        </span>
        <span className="inline-flex items-center gap-1"
              style={{ color: meetup.visibility === "friends" ? "#FFD700" : "#F2DC8C" }}>
          {meetup.visibility === "friends" ? <LockKey size={9}/> : <Globe size={9}/>}
          {meetup.visibility === "friends" ? "Friends" : "Public"}
        </span>
      </div>
    </Link>
  );
}


function MeetupDetail({ mid }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [m, setM] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/meetups/${mid}`);
      setM(data);
    } catch { setM(null); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [mid]);

  const isOwner = m && user && m.owner_id === user.user_id;
  const toggleAttend = async () => {
    setBusy(true);
    try {
      if (m.is_attending) await api.post(`/meetups/${mid}/leave`);
      else await api.post(`/meetups/${mid}/join`);
      load();
    } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm("Delete this meet-up?")) return;
    await api.delete(`/meetups/${mid}`);
    navigate("/app/meetups");
  };
  const save = async (payload) => {
    await api.put(`/meetups/${mid}`, payload);
    setEditOpen(false); load();
  };

  if (loading) return <Loading/>;
  if (!m) return <NotFound label="Meet-up not found" backTo="/app/meetups"/>;

  return (
    <div className="max-w-3xl mx-auto space-y-5" data-testid="meetup-detail">
      <Link to="/app/meetups" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#F2DC8C]/70 hover:text-[#FFD700]">
        <ArrowLeft size={12}/> All Meet-ups
      </Link>

      <div className="rounded-2xl p-6 text-center"
           style={{
             background: "radial-gradient(circle at 50% 0%, rgba(255,215,0,0.14) 0%, transparent 55%)," +
                         "linear-gradient(180deg, rgba(26,18,6,0.85) 0%, rgba(5,3,0,0.95) 100%)",
             border: "1px solid rgba(255,215,0,0.4)",
             boxShadow: "0 12px 40px rgba(0,0,0,0.55)",
           }}>
        <div className="flex items-center gap-1.5 justify-center flex-wrap">
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-display uppercase tracking-widest"
                style={{ background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)", color: "#0a0703" }}>
            Meet-up
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] uppercase tracking-widest"
                style={{ border: `1px solid ${m.visibility === "friends" ? "#FFD700" : "rgba(212,175,55,0.55)"}`,
                         color: m.visibility === "friends" ? "#FFD700" : "#F2DC8C" }}>
            {m.visibility === "friends" ? <><LockKey size={9}/> Friends</> : <><Globe size={9}/> Public</>}
          </span>
        </div>
        <h1 className="mt-3 font-display text-[30px] leading-tight"
            style={{
              background: "linear-gradient(180deg, #FFF3AE 0%, #FFD700 45%, #8a6a00 100%)",
              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
            }}>
          {m.title}
        </h1>
        <Countdown target={m.event_at}/>
        <div className="mt-3 text-[13px] text-[#F2DC8C] space-y-0.5">
          <div><span className="text-[#FFD700] font-display">When:</span> {new Date(m.event_at).toLocaleString()}</div>
          <div><span className="text-[#FFD700] font-display">Where:</span> {m.location}</div>
          <div><span className="text-[#FFD700] font-display">Host:</span> <UserLink username={m.owner_username}>@{m.owner_username}</UserLink></div>
        </div>
        {m.description && (
          <p className="mt-4 text-[13px] text-[#F2DC8C]/80 leading-relaxed whitespace-pre-wrap max-w-2xl mx-auto">
            {m.description}
          </p>
        )}

        <div className="mt-5 flex items-center justify-center gap-2 flex-wrap">
          {!isOwner && (
            <button onClick={toggleAttend} disabled={busy} data-testid="meetup-join"
                    className="inline-flex items-center gap-2 h-11 px-6 rounded-xl font-display uppercase text-[12px] tracking-[0.22em] disabled:opacity-50"
                    style={m.is_attending
                      ? { border: "1px solid rgba(212,175,55,0.55)", color: "#F2DC8C" }
                      : { background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)", color: "#0a0703",
                          boxShadow: "0 6px 20px rgba(255,215,0,0.35)" }}>
              {m.is_attending ? "Leave" : "I'm In"}
            </button>
          )}
          {isOwner && (
            <>
              <button onClick={() => setEditOpen(true)} data-testid="meetup-edit"
                      className="inline-flex items-center gap-1.5 h-11 px-4 rounded-xl text-[11px] uppercase tracking-widest"
                      style={{ border: "1px solid rgba(212,175,55,0.55)", color: "#F2DC8C" }}>
                <PencilSimple size={11}/> Edit
              </button>
              <button onClick={remove} data-testid="meetup-delete"
                      className="inline-flex items-center gap-1.5 h-11 px-4 rounded-xl text-[11px] uppercase tracking-widest text-[#FF5D73]"
                      style={{ border: "1px solid rgba(255,93,115,0.4)" }}>
                <Trash size={11}/> Delete
              </button>
            </>
          )}
          <AddToCalendarButton
            title={m.title}
            start={m.event_at}
            description={m.description}
            location={m.location}
            testId="meetup-gcal"
          />
        </div>
      </div>

      {/* Attendees */}
      <section>
        <div className="text-[11px] uppercase tracking-[0.28em] text-[#F2DC8C]/75 mb-3">
          Going · {m.attendee_count}
        </div>
        <div className="flex flex-wrap gap-2">
          {(m.attendees || []).map((a) => (
            <UserLink key={a.user_id} username={a.username} testId={`attendee-${a.user_id}`}
                      className="inline-flex items-center gap-2 pr-3 pl-1 py-1 rounded-full transition-colors"
                      stopPropagation={false}>
              <span className="inline-flex items-center gap-2 pr-3 pl-1 py-1 rounded-full"
                   style={{ background: "rgba(0,0,0,0.4)", border: "1px solid rgba(212,175,55,0.4)" }}>
                <span className="w-7 h-7 rounded-full flex items-center justify-center overflow-hidden"
                      style={{ border: "1px solid #D4AF37" }}>
                  {a.avatar_image ? (
                    <img src={`${API}/files/${a.avatar_image}`} alt="" className="w-full h-full object-cover"/>
                  ) : (
                    <span className="text-[11px] font-display text-[#FFD700]">
                      {(a.name || a.username || "?").slice(0, 1).toUpperCase()}
                    </span>
                  )}
                </span>
                <span className="text-[12px] text-[#F2DC8C]">{a.name?.split(" ")[0] || "@" + a.username}</span>
              </span>
            </UserLink>
          ))}
        </div>
      </section>

      <AnimatePresence>
        {editOpen && (
          <MeetupModal meetup={m} onClose={() => setEditOpen(false)} onSave={save}/>
        )}
      </AnimatePresence>
    </div>
  );
}


function MeetupModal({ meetup, onClose, onSave }) {
  const [title, setTitle] = useState(meetup?.title || "");
  const [description, setDescription] = useState(meetup?.description || "");
  const [location, setLocation] = useState(meetup?.location || "");
  const [when, setWhen] = useState(meetup?.event_at ? meetup.event_at.slice(0, 16) : "");
  const [visibility, setVisibility] = useState(meetup?.visibility || "public");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (!title.trim() || !location.trim() || !when) return;
    setBusy(true);
    try { await onSave({ title, description: description || null, location, event_at: when, visibility }); }
    finally { setBusy(false); }
  };

  return (
    <FormModal title={meetup ? "Edit Meet-up" : "New Meet-up"} icon={UsersThree} onClose={onClose} onSubmit={submit}
               busy={busy} disabled={!title.trim() || !location.trim() || !when} testid="meetup-modal"
               saveLabel={meetup ? "Save" : "Create"}>
      <FormField label="Title">
        <input autoFocus data-testid="meetup-title" value={title}
               onChange={(e) => setTitle(e.target.value.slice(0, 140))}
               placeholder="Sunset beach hang"
               className={inputCls}/>
      </FormField>
      <FormField label="When">
        <input type="datetime-local" data-testid="meetup-when" value={when}
               onChange={(e) => setWhen(e.target.value)}
               className={inputCls} style={{ colorScheme: "dark" }}/>
      </FormField>
      <FormField label="Where">
        <input data-testid="meetup-loc" value={location}
               onChange={(e) => setLocation(e.target.value.slice(0, 300))}
               placeholder="Hana Bay, Maui"
               className={inputCls}/>
      </FormField>
      <FormField label="Description (optional)">
        <textarea data-testid="meetup-desc" value={description} rows={3}
                  onChange={(e) => setDescription(e.target.value.slice(0, 4000))}
                  placeholder="Bring the vibes."
                  className={inputCls + " resize-none"}/>
      </FormField>
      <FormField label="Visibility">
        <div className="flex gap-2">
          {[["public","Public",Globe],["friends","Friends only",LockKey]].map(([k,l,Icon]) => (
            <button key={k} type="button" onClick={() => setVisibility(k)}
                    data-testid={`meetup-vis-${k}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] uppercase tracking-widest"
                    style={visibility === k
                      ? { background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)", color: "#0a0703" }
                      : { border: "1px solid rgba(212,175,55,0.45)", color: "#F2DC8C" }}>
              <Icon size={10}/> {l}
            </button>
          ))}
        </div>
      </FormField>
    </FormModal>
  );
}


// ── Shared UI primitives (used by MeetupsPage AND EventsPage/JobsPage) ──
export const inputCls =
  "w-full bg-transparent text-[13px] text-[#F2DC8C] placeholder:text-[#F2DC8C]/40 focus:outline-none";

export function Header({ title, sub, actionLabel, onAction, testid }) {
  return (
    <header className="flex flex-wrap items-end justify-between gap-3" data-testid={`${testid}-header`}>
      <div>
        <div className="text-[11px] uppercase tracking-[0.32em] text-[#F2DC8C]/60">{sub}</div>
        <h1 className="mt-1 font-display text-[34px] leading-tight"
            style={{
              background: "linear-gradient(180deg, #FFF3AE 0%, #FFD700 45%, #8a6a00 100%)",
              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
              letterSpacing: "0.02em",
            }}>
          {title}
        </h1>
      </div>
      {actionLabel && (
        <button onClick={onAction}
                data-testid={`${testid}-new`}
                className="inline-flex items-center gap-2 h-10 px-4 rounded-xl font-display uppercase text-[11px] tracking-[0.22em] transition-transform active:scale-95"
                style={{
                  background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)",
                  color: "#0a0703",
                  boxShadow: "0 6px 20px rgba(255,215,0,0.35)",
                }}>
          <Plus size={12} weight="bold"/> {actionLabel}
        </button>
      )}
    </header>
  );
}

export function ScopeTabs({ scope, onChange, options, testid }) {
  return (
    <div className="inline-flex rounded-xl p-1" data-testid={testid}
         style={{ border: "1px solid rgba(212,175,55,0.45)", background: "rgba(0,0,0,0.4)" }}>
      {options.map(([k, label]) => (
        <button key={k} onClick={() => onChange(k)}
                data-testid={`${testid}-${k}`}
                className="px-3 h-8 rounded-lg font-display uppercase text-[10px] tracking-[0.22em] transition-all"
                style={scope === k
                  ? { background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)", color: "#0a0703" }
                  : { color: "#F2DC8C" }}>
          {label}
        </button>
      ))}
    </div>
  );
}

export function Loading() {
  return (
    <div className="rounded-2xl p-10 text-center"
         style={{ border: "1px solid rgba(212,175,55,0.35)", background: "rgba(0,0,0,0.4)" }}>
      <CircleNotch size={22} className="animate-spin text-[#FFD700] mx-auto"/>
    </div>
  );
}

export function Empty({ label, onCreate, cta, icon: Icon = UsersThree }) {
  return (
    <div className="rounded-2xl p-10 text-center"
         style={{ background: "linear-gradient(180deg, rgba(26,18,6,0.75) 0%, rgba(5,3,0,0.9) 100%)",
                  border: "1px solid rgba(212,175,55,0.45)" }}>
      <div className="mx-auto w-14 h-14 rounded-2xl flex items-center justify-center"
           style={{ background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)" }}>
        <Icon size={22} weight="fill" color="#0a0703"/>
      </div>
      <div className="mt-4 font-display text-[22px] text-[#FFD700]">{label}</div>
      <button onClick={onCreate}
              className="mt-4 inline-flex items-center gap-2 h-10 px-5 rounded-xl font-display uppercase text-[11px] tracking-[0.22em]"
              style={{ background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)", color: "#0a0703" }}>
        <Plus size={12} weight="bold"/> {cta}
      </button>
    </div>
  );
}

export function NotFound({ label, backTo }) {
  return (
    <div className="max-w-2xl mx-auto rounded-2xl p-10 text-center"
         style={{ border: "1px solid rgba(255,93,115,0.4)", background: "rgba(0,0,0,0.5)" }}>
      <div className="font-display text-[24px] text-[#FF5D73]">{label}</div>
      <Link to={backTo} className="mt-4 inline-block text-[11px] uppercase tracking-widest text-[#FFD700]">Back</Link>
    </div>
  );
}

export function Countdown({ target }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const ms = Math.max(0, new Date(target).getTime() - now);
  const s = Math.floor(ms / 1000);
  const parts = [
    { n: Math.floor(s / 86400), label: "Days" },
    { n: Math.floor((s % 86400) / 3600), label: "Hours" },
    { n: Math.floor((s % 3600) / 60), label: "Mins" },
    { n: s % 60, label: "Secs" },
  ];
  return (
    <div className="mt-4 flex flex-col items-center gap-2">
      <div className="text-[10px] uppercase tracking-[0.32em] text-[#F2DC8C]/70">
        {ms === 0 ? "Happening now" : "Starts in"}
      </div>
      {ms > 0 && (
        <div className="flex items-center gap-2 justify-center flex-wrap">
          {parts.map((p) => (
            <div key={p.label} className="flex flex-col items-center min-w-[50px] px-2 py-1.5 rounded-lg"
                 style={{
                   background: "linear-gradient(180deg, #FFF3AE, #D4AF37 55%, #6b4d00)",
                   boxShadow: "0 4px 14px rgba(255,215,0,0.35)",
                   color: "#0a0703",
                 }}>
              <span className="font-display text-[22px] leading-none tabular-nums">{String(p.n).padStart(2, "0")}</span>
              <span className="text-[8px] uppercase tracking-widest opacity-80 mt-0.5">{p.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function FormField({ label, children }) {
  return (
    <label className="block rounded-xl p-3"
           style={{ border: "1px solid rgba(212,175,55,0.35)", background: "rgba(0,0,0,0.35)" }}>
      <div className="text-[9px] uppercase tracking-[0.22em] text-[#F2DC8C]/70 mb-1">{label}</div>
      {children}
    </label>
  );
}

export function FormModal({ title, icon: Icon, onClose, onSubmit, busy, disabled, children, saveLabel = "Save", testid }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                onClick={onClose}
                className="fixed inset-0 z-[110] flex items-center justify-center p-4"
                style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(14px)" }}
                data-testid={testid}>
      <motion.form initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
                   onClick={(e) => e.stopPropagation()} onSubmit={onSubmit}
                   className="w-full max-w-lg rounded-2xl overflow-hidden"
                   style={{
                     background: "linear-gradient(180deg, rgba(20,14,4,0.98) 0%, rgba(5,3,0,1) 100%)",
                     border: "1.5px solid rgba(255,215,0,0.55)",
                     boxShadow: "0 30px 80px rgba(0,0,0,0.75)",
                     maxHeight: "88vh",
                   }}>
        <div className="flex items-center gap-3 px-5 py-4 border-b" style={{ borderColor: "rgba(212,175,55,0.35)" }}>
          {Icon && <Icon size={16} weight="fill" className="text-[#FFD700]"/>}
          <div className="font-display text-[16px] text-[#FFD700]">{title}</div>
          <button type="button" onClick={onClose} className="ml-auto w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{ border: "1px solid rgba(212,175,55,0.5)" }}>
            <X size={13} className="text-[#FFD700]"/>
          </button>
        </div>
        <div className="p-5 space-y-3 overflow-y-auto" style={{ maxHeight: "68vh" }}>{children}</div>
        <div className="flex items-center justify-end gap-2 p-4 border-t" style={{ borderColor: "rgba(212,175,55,0.35)" }}>
          <button type="button" onClick={onClose}
                  className="px-4 h-10 rounded-xl text-[11px] uppercase tracking-widest text-[#F2DC8C]"
                  style={{ border: "1px solid rgba(212,175,55,0.45)" }}>Cancel</button>
          <button type="submit" disabled={busy || disabled}
                  data-testid={`${testid}-save`}
                  className="px-4 h-10 rounded-xl font-display uppercase text-[11px] tracking-[0.22em] disabled:opacity-40"
                  style={{ background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)", color: "#0a0703" }}>
            {busy ? "Saving…" : saveLabel}
          </button>
        </div>
      </motion.form>
    </motion.div>
  );
}
